import type {
  BulkStoreProductInput,
  CreateContactInput,
  CreateCategoryInput,
  CreateDistributionInput,
  CreateFeedbackInput,
  CreateGoalInput,
  CreateNetworkInput,
  CreateProductAliasInput,
  CreateProductInput,
  CreateShipmentSourceProductInput,
  CreateProductionCostInput,
  CreateReorderOverrideInput,
  CreateSalePriceInput,
  CreateStoreAliasInput,
  ShipmentSourceMatchInput,
  CreateStoreInput,
  CreateThresholdOverrideInput,
  CreateUnitConversionInput,
  UpdateContactInput,
  UpdateCategoryInput,
  UpdateNetworkInput,
  UpdateProductInput,
  UpdateStoreInput,
  UpsertPreferenceInput,
} from '@kjotkompanid/contracts';
import { request } from './api';
import type {
  AuditRecord,
  NetworkRecord,
  PageResult,
  PreferenceRecord,
  ProductDetailResponse,
  ProductListItem,
  ShipmentProductIntakeCandidate,
  ReferenceData,
  StoreDetailResponse,
  StoreListItem,
} from './master-data-types';

const json = (method: string, body: object): RequestInit => ({
  method,
  body: JSON.stringify(body),
});

const queryString = (values: Record<string, string | number | undefined>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined && value !== '') query.set(key, String(value));
  }
  return query.toString();
};

export const masterApi = {
  reference: () => request<ReferenceData>('master/reference'),
  createCategory: (body: CreateCategoryInput) =>
    request<{ id: string; revision: number }>('master/categories', json('POST', body)),
  updateCategory: (id: string, body: UpdateCategoryInput) =>
    request<{ revision: number }>(`master/categories/${id}`, json('PATCH', body)),
  networks: () => request<NetworkRecord[]>('master/networks'),
  createNetwork: (body: CreateNetworkInput) =>
    request<{ id: string; revision: number }>('master/networks', json('POST', body)),
  updateNetwork: (id: string, body: UpdateNetworkInput) =>
    request<{ revision: number }>(`master/networks/${id}`, json('PATCH', body)),
  products: (filters: Record<string, string | number | undefined>) =>
    request<PageResult<ProductListItem>>(`master/products?${queryString(filters)}`),
  product: (id: string) => request<ProductDetailResponse>(`master/products/${id}`),
  shipmentProductIntake: (status?: string) =>
    request<ShipmentProductIntakeCandidate[]>(
      `master/shipment-product-intake?${queryString({ status })}`,
    ),
  createProduct: (body: CreateProductInput) =>
    request<{ id: string; revision: number }>('master/products', json('POST', body)),
  createShipmentSourceProduct: (body: CreateShipmentSourceProductInput) =>
    request<{ id: string; revision: number }>(
      'master/shipment-source-products',
      json('POST', body),
    ),
  autoCreateShipmentSourceProduct: (body: CreateShipmentSourceProductInput) =>
    request<{ id: string; revision: number }>(
      'master/shipment-source-products/auto-create',
      json('POST', body),
    ),
  updateProduct: (id: string, body: UpdateProductInput) =>
    request<{ revision: number }>(`master/products/${id}`, json('PATCH', body)),
  addProductAlias: (id: string, body: CreateProductAliasInput) =>
    request<{ id: string }>(`master/products/${id}/aliases`, json('POST', body)),
  matchShipmentSourceSku: (id: string, body: ShipmentSourceMatchInput) =>
    request<{ id: string }>(`master/products/${id}/shipment-source-match`, json('POST', body)),
  setProductAliasStatus: (id: string, status: 'ACTIVE' | 'INACTIVE') =>
    request<{ updated: true }>(`master/product-aliases/${id}/status`, json('PATCH', { status })),
  addConversion: (id: string, body: CreateUnitConversionInput) =>
    request<{ id: string }>(`master/products/${id}/conversions`, json('POST', body)),
  stores: (filters: Record<string, string | number | undefined>) =>
    request<PageResult<StoreListItem>>(`master/stores?${queryString(filters)}`),
  store: (id: string) => request<StoreDetailResponse>(`master/stores/${id}`),
  createStore: (body: CreateStoreInput) =>
    request<{ id: string; revision: number }>('master/stores', json('POST', body)),
  updateStore: (id: string, body: UpdateStoreInput) =>
    request<{ revision: number }>(`master/stores/${id}`, json('PATCH', body)),
  addStoreAlias: (id: string, body: CreateStoreAliasInput) =>
    request<{ id: string }>(`master/stores/${id}/aliases`, json('POST', body)),
  setStoreAliasStatus: (id: string, status: 'ACTIVE' | 'INACTIVE') =>
    request<{ updated: true }>(`master/store-aliases/${id}/status`, json('PATCH', { status })),
  extendStoreAliasHistory: (id: string, effectiveFrom: string) =>
    request<Record<string, never>>(
      `master/store-aliases/${id}/history`,
      json('PATCH', { effectiveFrom }),
    ),
  createContact: (storeId: string, body: CreateContactInput) =>
    request<{ id: string; revision: number }>(
      `master/stores/${storeId}/contacts`,
      json('POST', body),
    ),
  updateContact: (contactId: string, body: UpdateContactInput) =>
    request<{ revision: number }>(`master/contacts/${contactId}`, json('PATCH', body)),
  addFeedback: (storeId: string, body: CreateFeedbackInput) =>
    request<{ id: string }>(`master/stores/${storeId}/feedback`, json('POST', body)),
  bulkStoreProducts: (body: BulkStoreProductInput) =>
    request<{ updated: number }>('master/store-products/bulk', json('POST', body)),
  addDistribution: (productId: string, body: CreateDistributionInput) =>
    request<{ id: string }>(`master/products/${productId}/distributions`, json('POST', body)),
  addSalePrice: (productId: string, body: CreateSalePriceInput) =>
    request<{ id: string }>(`master/products/${productId}/sale-prices`, json('POST', body)),
  addProductionCost: (productId: string, body: CreateProductionCostInput) =>
    request<{ id: string }>(`master/products/${productId}/production-costs`, json('POST', body)),
  addGoal: (productId: string, body: CreateGoalInput) =>
    request<{ id: string }>(`master/products/${productId}/goals`, json('POST', body)),
  addReorder: (productId: string, body: CreateReorderOverrideInput) =>
    request<{ id: string }>(`master/products/${productId}/reorder-overrides`, json('POST', body)),
  addThreshold: (productId: string, body: CreateThresholdOverrideInput) =>
    request<{ id: string }>(`master/products/${productId}/threshold-overrides`, json('POST', body)),
  preference: (namespace: string) => request<PreferenceRecord>(`master/preferences/${namespace}`),
  savePreference: (namespace: string, body: UpsertPreferenceInput) =>
    request<{ revision: number }>(`master/preferences/${namespace}`, json('PUT', body)),
  audit: (entityType: string, entityId: string) =>
    request<AuditRecord[]>(`master/audit/${entityType}/${encodeURIComponent(entityId)}`),
};
